import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface BillingData {
  balance?: number;
}

export function ArtistEditBalance({ artistId }: { artistId: string }) {
  const [billing, setBilling] = useState<BillingData>({
    balance: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/admin/artists/${artistId}/billing`);

        if (response.ok) {
          const data = await response.json();
          if (data.billing) {
            const balance = data.billing.balance || 0;
            const roundedBalance = Math.round(balance * 100) / 100;
            setBilling({
              balance: roundedBalance,
            });
          } else {
            setBilling({ balance: 0 });
          }
        } else {
          console.error("Failed to fetch billing information");
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [artistId]);

  const handleSave = async () => {
    if (billing.balance === undefined || billing.balance < 0) {
      alert("Balance must be a positive number");
      return;
    }

    setIsSaving(true);
    try {
      const balanceInDollars = Math.round(billing.balance * 100) / 100;

      const response = await fetch(`/api/admin/artists/${artistId}/billing`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ balance: balanceInDollars }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save billing information");
      }

      alert("Balance saved successfully");
    } catch (error) {
      console.error("Error saving billing information:", error);
      alert(error instanceof Error ? error.message : "Failed to save billing information");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div className="space-y-2">
        <Label htmlFor="balance">Balance ($)</Label>
        <Input
          id="balance"
          type="number"
          step="0.01"
          min="0"
          value={billing.balance !== undefined ? billing.balance : ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || value === ".") {
              setBilling({ ...billing, balance: undefined });
              return;
            }
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
              const rounded = Math.round(numValue * 100) / 100;
              setBilling({ ...billing, balance: rounded });
            }
          }}
          onBlur={(e) => {
            const value = e.target.value;
            if (value !== "" && value !== ".") {
              const numValue = parseFloat(value);
              if (!isNaN(numValue)) {
                const rounded = Math.round(numValue * 100) / 100;
                setBilling({ ...billing, balance: rounded });
              }
            }
          }}
          placeholder="Enter balance"
        />
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
